import type { ChildId } from './valueObjects/childId';
import type { LateDateTime } from './valueObjects/lateDateTime';
import type { Memo } from './valueObjects/memo';
import type { NurseryId } from './valueObjects/nurseryId';
import type { ParentId } from './valueObjects/parentId';
import type { AttendContactLateJsonPub, AttendContactLateJsonPvt } from '@/types/domain/attendContact/entities/attendContactLateEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 遅刻連絡プロパティ非公開
 */
export type AttendContactLatePropsPvt = {
  /** 施設ID */
  nurseryId: NurseryId,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 遅刻日時 */
  lateDateTime: LateDateTime,
  /** メモ */
  memo: Memo,
}

/**
 * 遅刻連絡プロパティ公開
 */
export type AttendContactLatePropsPub = Record<string, never>

/**
 * 遅刻連絡Entity
 */
export class AttendContactLateEntity extends EntityPub<
  AttendContactLatePropsPvt,
  AttendContactLateJsonPvt,
  AttendContactLatePropsPub,
  AttendContactLateJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  get deleteKey(): string {
    return this.getFutureYear(this.lateDateTime.value, 1).toJSON();
  }

  toJsonPubData(): AttendContactLateJsonPub {
    return {};
  }

  toJsonData(): AttendContactLateJsonPvt {
    const p: AttendContactLateJsonPvt = {
      nurseryId: this.nurseryId.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      lateDateTime: this.lateDateTime.value.toJSON(),
      memo: this.memo.value,
    };
    return p;
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryAttendContactAttendContactLateEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    const ymd = this.lateDateTime.toStringDate();
    return `${ymd}#${this.parentId.value}_${this.childId.value}#${this.nurseryId.value}`;
  }

  /**
   * サブキー
   */
  get subKey(): string | null {
    const ymd = this.lateDateTime.toStringDate();
    return `${this.parentId.value}_${this.childId.value}#${ymd}#${this.nurseryId.value}`;
  }

  /** 施設ID */
  get nurseryId(): NurseryId {
    return this._value.nurseryId;
  }

  /** 施設ID */
  set nurseryId(nurseryId: NurseryId) {
    this._value.nurseryId = nurseryId;
  }

  /** 親ID */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /** 親ID */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /** 子ID */
  get childId(): ChildId {
    return this._value.childId;
  }

  /** 子ID */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }

  /** 遅刻日時 */
  get lateDateTime(): LateDateTime {
    return this._value.lateDateTime;
  }

  /** 遅刻日時 */
  set lateDateTime(lateDateTime: LateDateTime) {
    this._value.lateDateTime = lateDateTime;
  }

  /** メモ */
  get memo(): Memo {
    return this._value.memo;
  }

  /** メモ */
  set memo(memo: Memo) {
    this._value.memo = memo;
  }
}
